import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { WSImagePlayer, WowzaWebRTCPlayer, WebRTCPlayer } from "orion-components/CBComponents";
import { Typography } from "@mui/material";
import { Translate } from "orion-components/i18n";
import { useDispatch, useSelector } from "react-redux";
import { activityService, cameraService } from "client-app-core";
import { initComponentInstance } from "orion-components/SharedComponents/ComponentState/redux/actions";
import VideoTopBar from "./components/VideoTopBar";
import VideoBottomBar from "./components/VideoBottomBar";
import AvigilonWebRTCPlayer from "../Video/WebRTCPlayer/Avigilon/AvigilonWebRTCPlayer";
import VicadsLiveWebRTCPlayer from "../Video/WebRTCPlayer/Vicads/VicadsLiveWebRTCPlayer";
import VicadsRecordedWebRTCPlayer from "../Video/WebRTCPlayer/Vicads/VicadsRecordedWebRTCPlayer";
import VideoIntegratedControls from "../../Profiles/Widgets/PTZControls/VideoIntegratedControls";
import FlashphonerRecordedWebRTCPlayer from "../Video/WebRTCPlayer/Flashphoner/FlashphonerRecordedWebRTCPlayer";
import VideoTranslatorPlayer from "../Video/WebRTCPlayer/VideoTranslator/Player";
import { fitAspectInsideContainer, useElementSize, getStreamURL, getPlaybackURL, usePrevious } from "./utils";
import { getCanControlPTZ } from "../../GlobalData/PTZPrivilege/selectors";

const propTypes = {
	camera: PropTypes.shape({
		id: PropTypes.string.isRequired,
		preset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		player: PropTypes.shape({
			type: PropTypes.string.isRequired,
			url: PropTypes.string,
			replayType: PropTypes.string,
			api: PropTypes.string,
			backend: PropTypes.string
		}),
		entityData: PropTypes.object
	}).isRequired,
	inDock: PropTypes.bool,
	fillAvailable: PropTypes.bool,
	setCameraPriority: PropTypes.func,
	instanceId: PropTypes.string,
	entityId: PropTypes.string,
	entityType: PropTypes.string,
	namePrefix: PropTypes.string,
	dockedItemId: PropTypes.string,
	isDockedVideoTour: PropTypes.bool,
	preload: PropTypes.bool,
	tourMode: PropTypes.bool,
	tourPaused: PropTypes.bool,
	readOnly: PropTypes.bool,
	videoPlayerHovered: PropTypes.bool,
	playbackStartTime: PropTypes.oneOfType([PropTypes.date, PropTypes.object]),
	playBarValue: PropTypes.oneOfType([PropTypes.date, PropTypes.object]),
	playbackPlaying: PropTypes.bool,
	currentReplayMedia: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	addReplayMedia: PropTypes.func,
	removeReplayMedia: PropTypes.func,
	expanded: PropTypes.bool,
	isInFullScreen: PropTypes.bool,
	isOnAlertProfile: PropTypes.bool,
	isInVideoWall: PropTypes.bool,
	alertDateTime: PropTypes.any,
	removeFromWall: PropTypes.func,
	toggleFullScreenCameraDialog: PropTypes.func,
	dialogContainerRef: PropTypes.shape({
		current: PropTypes.any
	}),
	setTourPaused: PropTypes.func,
	isOnProfile: PropTypes.bool,
	defaultToRecorded: PropTypes.bool,
	playbackStartOffset: PropTypes.number,
	initialRecordedDuration: PropTypes.string,
	initialRecordedTimeUnit: PropTypes.string,
	clusterProps: PropTypes.object,
	hideRemoveFromWall: PropTypes.bool,
	disableVideoModeToggle: PropTypes.bool
};

const defaultProps = {
	camera: { player: { url: null } },
	inDock: false,
	fillAvailable: false,
	setCameraPriority: () => {},
	instanceId: "",
	entityId: "",
	entityType: "",
	namePrefix: null,
	dockedItemId: null,
	isDockedVideoTour: false,
	preload: false,
	tourMode: false,
	tourPaused: false,
	videoPlayerHovered: false,
	expanded: false,
	isOnAlertProfile: false,
	isInVideoWall: false,
	isOnProfile: false,
	alertDateTime: "",
	defaultToRecorded: false,
	disableVideoModeToggle: false
};

const styles = {
	frame: {
		position: "relative",
		backgroundColor: "#000",
		width: "100%",
		height: "100%",
		maxWidth: "100%",
		maxHeight: "100%",
		overflow: "hidden"
	}
};

const COMPONENT_KEY = "cbComponents.newVideoPlayer.videoPlayerContainer";

// streamUrl isn't used by "file" playback, nor by eclipse-backed "native" cameras
// (VideoTranslatorPlayer resolves its own eclipseStreamId for those instead).
const shouldSkipStreamUrlFetch = player =>
	["ws-jpeg", "wowza", "avigilon", "vicads", "file"].includes(player?.type) ||
	(player?.type === "native" && player?.backend === "eclipse");

const LiveVideoPlayer = props => {
	const {
		camera,
		instanceId,
		entityId,
		entityType,
		namePrefix,
		dockedItemId,
		isDockedVideoTour,
		preload,
		tourMode,
		tourPaused,
		readOnly,
		videoPlayerHovered,
		playbackStartTime,
		playBarValue,
		playbackPlaying,
		currentReplayMedia,
		addReplayMedia,
		removeReplayMedia,
		inDock,
		expanded,
		isInFullScreen,
		isOnAlertProfile,
		isInVideoWall,
		removeFromWall,
		toggleFullScreenCameraDialog,
		dialogContainerRef,
		isOnProfile,
		alertDateTime,
		defaultToRecorded,
		playbackStartOffset,
		initialRecordedDuration,
		initialRecordedTimeUnit,
		clusterProps,
		hideRemoveFromWall,
		setTourPaused,
		disableVideoModeToggle
	} = props;
	const dispatch = useDispatch();

	const [initDimensionsDetected, setInitDimensionsDetected] = useState(false);
	const [streamUrl, setStreamUrl] = useState(null);
	const [streamUrlError, setStreamUrlError] = useState(false);
	const [selectedVideoMode, setSelectedVideoMode] = useState("Live");
	const [playbackStartDateTime, setPlaybackStartDateTime] = useState("");
	const [playbackEndDateTime, setPlaybackEndDateTime] = useState("");
	const [playbackStreamName, setPlaybackStreamName] = useState("");
	const [playbackUrl, setPlaybackUrl] = useState(null);
	const [playbackUrlError, setPlaybackUrlError] = useState(false);
	const [resizeTo, setResizeTo] = useState("dock");
	const [selectedClip, setSelectedClip] = useState(null);
	const pendingBookmarkNavigationRef = useRef(null);
	const recordedNavigatorRef = useRef(null);
	const lastAppliedTourPresetRef = useRef(null);
	const loggedPlayerLoadsRef = useRef(new Set());

	const ptzLockEnabled = useSelector(state => getCanControlPTZ(state, { cameraId: camera.id }));
	const appId = useSelector(state => state.application?.appId);

	const logVideoPlayerAction = useCallback(
		(action, context = {}) => {
			if (!appId || !camera?.id) return;
			activityService.logUserAction(action, { appId, cameraId: camera.id, ...context }).catch(err => {
				console.warn(`Unable to log ${action} user action`, err);
			});
		},
		[appId, camera?.id]
	);

	useEffect(() => {
		if (preload || !appId || !camera?.id || loggedPlayerLoadsRef.current.has(camera.id)) return;
		loggedPlayerLoadsRef.current.add(camera.id);
		logVideoPlayerAction("video-player-loaded");
	}, [appId, camera?.id, logVideoPlayerAction, preload]);

	// Callback function to update startTime and endTime in parent
	const handleSetStartAndEndTime = (newStartTime, newEndTime) => {
		setPlaybackStartDateTime(newStartTime);
		setPlaybackEndDateTime(newEndTime);
	};

	const [, forceUpdate] = useState(0);
	const [dockContainerDims, setDockContainerDims] = useState({ width: "", height: "" });

	const frame = useRef(null);
	const frameStartHeight = useRef(null);
	const { bccContainerRef, size, updateDimensions } = useElementSize();

	const dockData = useSelector(state => state.appState?.dock?.dockData);
	const cameraDock = useSelector(state => state.appState?.dock?.cameraDock);

	const iscameraInDock =
		dockData.isOpen && dockData.tab === "Cameras" && cameraDock.dockedCameras.includes(camera.id);

	const docked = iscameraInDock && isOnProfile ? true : false;

	const prevProps = usePrevious(props);

	useEffect(() => {
		if (!instanceId) return; // optional guard

		dispatch(initComponentInstance(COMPONENT_KEY, instanceId, {}));
		dispatch(
			initComponentInstance(COMPONENT_KEY, instanceId, {
				components: { recorded: { jumpToDifferentTime: { isOpen: false } } }
			})
		);
	}, [dispatch, instanceId]);

	useEffect(() => {
		if (defaultToRecorded) {
			setSelectedVideoMode("recorded");
		} else {
			setSelectedVideoMode("Live");
		}
	}, [defaultToRecorded]);

	useEffect(() => {
		const fetchStreamUrl = async () => {
			if (!shouldSkipStreamUrlFetch(camera.player)) {
				const streamUrl = await getStreamURL(camera.id); // Await the promise
				if (streamUrl !== null) {
					setStreamUrl(streamUrl);
				} else {
					setStreamUrlError(true);
				}
			}
		};

		fetchStreamUrl();
	}, [camera]);

	useEffect(() => {
		if (prevProps) {
			const { id, player } = camera;
			const prevId = prevProps.camera ? prevProps.camera.id : null;

			if (id !== prevId && !shouldSkipStreamUrlFetch(player)) {
				const fetchStreamUrl = async () => {
					const streamUrl = await getStreamURL(camera.id); // Await the promise
					if (streamUrl !== null) {
						setStreamUrl(streamUrl);
					} else {
						setStreamUrlError(true);
					}
				};

				fetchStreamUrl();
			}
		}
	}, [props]);

	useEffect(() => {
		if (selectedVideoMode !== "Live") {
			const fetchPlaybackUrl = async () => {
				const start = playbackStartDateTime;
				const end = playbackEndDateTime;
				if (start && end) {
					const playbackUrl = await getPlaybackURL(camera.id, start, end);
					if (playbackUrl !== null) {
						setPlaybackUrl(playbackUrl);
					} else {
						setPlaybackUrlError(true);
					}
				}
			};

			fetchPlaybackUrl();
		}
	}, [selectedVideoMode, camera, playbackStartDateTime, playbackEndDateTime, camera.id]);

	useEffect(() => {
		if (selectedVideoMode !== "recorded") {
			setPlaybackEndDateTime(null);
			setPlaybackStartDateTime(null);
		}
	}, [selectedVideoMode]);

	useEffect(() => {
		if (preload) {
			lastAppliedTourPresetRef.current = null;
			return;
		}

		if (!tourMode || !camera?.id || !camera?.preset || selectedVideoMode !== "Live") {
			return;
		}

		const presetApplicationKey = `${camera.id}:${camera.preset}`;
		if (lastAppliedTourPresetRef.current === presetApplicationKey) {
			return;
		}

		lastAppliedTourPresetRef.current = presetApplicationKey;
		cameraService.gotoPreset(camera.id, camera.preset, err => {
			if (err) {
				console.error("Failed to apply saved video tour preset", err);
				lastAppliedTourPresetRef.current = null;
			}
		});
	}, [camera?.id, camera?.preset, preload, selectedVideoMode, tourMode]);

	const registerRecordedNavigator = useCallback(
		fn => {
			recordedNavigatorRef.current = fn;
			if (!fn || selectedVideoMode.toLowerCase() !== "recorded" || !pendingBookmarkNavigationRef.current) {
				return;
			}

			const bookmarkTime = pendingBookmarkNavigationRef.current;
			pendingBookmarkNavigationRef.current = null;
			fn(bookmarkTime);
		},
		[selectedVideoMode]
	);

	const handleBookmarkSelect = useCallback(
		bookmark => {
			const bookmarkTime = new Date(bookmark?.timestamp);
			if (Number.isNaN(bookmarkTime.getTime())) {
				return;
			}
			if (bookmark?.id) {
				logVideoPlayerAction("video-bookmark-loaded", { bookmarkId: bookmark.id });
			}

			setSelectedClip(null);
			if (selectedVideoMode.toLowerCase() !== "recorded") {
				pendingBookmarkNavigationRef.current = bookmarkTime;
				setSelectedVideoMode("recorded");
				return;
			}

			if (recordedNavigatorRef.current) {
				recordedNavigatorRef.current(bookmarkTime);
			}
		},
		[logVideoPlayerAction, selectedVideoMode]
	);

	const handleClipSelect = useCallback(
		clip => {
			const clipStartTime = new Date(clip?.startTime);
			const clipEndTime = new Date(clip?.endTime);
			if (
				Number.isNaN(clipStartTime.getTime()) ||
				Number.isNaN(clipEndTime.getTime()) ||
				clipEndTime <= clipStartTime
			) {
				return;
			}
			if (clip?.id) {
				logVideoPlayerAction("video-clip-loaded", { clipId: clip.id });
			}

			setSelectedClip(clip);
			setSelectedVideoMode("recorded");
		},
		[logVideoPlayerAction]
	);

	const getPlaybackStreamName = useCallback(streamName => {
		setPlaybackStreamName(streamName);
	}, []);

	const handleRecordedTimeframeChange = useCallback(
		(startTime, endTime) => {
			logVideoPlayerAction("recorded-video-timeframe-changed", { startTime, endTime });
		},
		[logVideoPlayerAction]
	);

	const hasFeature = feature => {
		return camera.entityData.properties.features && camera.entityData.properties.features.includes(feature);
	};

	const getPlayer = () => {
		const { player, id } = camera;
		// -- handle cameras that have different player types for live and replay streaming
		const type =
			(readOnly || selectedVideoMode.toLowerCase() === "recorded") && player?.replayType
				? player?.replayType
				: player?.type;
		switch (type) {
			case "native":
				return player ? (
					<VideoTranslatorPlayer
						instanceId={instanceId}
						camera={camera}
						player={player}
						streamUrl={streamUrl}
						selectedVideoMode={selectedVideoMode.toLowerCase()}
					/>
				) : null;
			case "wowza":
				return (
					<WowzaWebRTCPlayer
						camera={camera}
						isForPlayback={selectedVideoMode === "recorded" ? true : false}
						start={playbackStartDateTime}
						end={playbackEndDateTime}
						getPlaybackStreamName={getPlaybackStreamName}
						isOnAlertProfile={isOnAlertProfile}
						contextId={entityId}
					/>
				);
			case "flashphoner": {
				if (selectedVideoMode === "Live") {
					return <WebRTCPlayer url={player?.url} streamUrl={streamUrl} streamUrlError={streamUrlError} />;
				} else {
					return (
						<FlashphonerRecordedWebRTCPlayer
							url={player?.url}
							playbackUrl={playbackUrl}
							playbackUrlError={playbackUrlError}
							onSetStartAndEndTime={handleSetStartAndEndTime} data-testid="orion-components-cbcomponents-new-video-player-live-video-player-flashphoner-recorded-web-rtcplayer"
						/>
					);
				}
			}

			case "ws-jpeg":
				return (
					<WSImagePlayer
						cameraId={id}
						videoProfile="desktop"
						resizeTo={resizeTo}
						instanceId={instanceId}
						entityId={entityId}
						entityType={entityType}
						playbackStartTime={readOnly && playbackStartTime ? playbackStartTime : null}
						playBarValue={readOnly && playBarValue ? playBarValue : null}
						playbackPlaying={readOnly && playbackPlaying ? playbackPlaying : null}
						currentReplayMedia={readOnly && currentReplayMedia ? currentReplayMedia : []}
						addReplayMedia={readOnly && addReplayMedia ? addReplayMedia : () => {}}
						removeReplayMedia={readOnly && removeReplayMedia ? removeReplayMedia : () => {}}
						selectedVideoMode={selectedVideoMode}
						api={camera.player.api || "liveStream"}
					/>
				);
			case "file":
				return (
					<video muted autoPlay loop style={{ maxHeight: "100%", maxWidth: "100%" }}>
						<source
							src={camera.entityData.properties.videoUrl}
							type={camera.entityData.properties.videoType}
						/>
					</video>
				);
			case "vicads": {
				if (selectedVideoMode === "Live") {
					return (
						<VicadsLiveWebRTCPlayer
							camera={camera}
							instanceId={instanceId}
							updateDimensions={updateDimensions}
							isInFullScreen={isInFullScreen}
							containerRef={frame}
						/>
					);
				} else {
					return (
						<VicadsRecordedWebRTCPlayer
							camera={camera}
							instanceId={instanceId}
							containerRef={frame}
							isInFullScreen={isInFullScreen}
							alertDateTime={alertDateTime}
							updateDimensions={updateDimensions}
						/>
					);
				}
			}
			case "avigilon": {
				return (
					<AvigilonWebRTCPlayer
						camera={camera}
						instanceId={instanceId}
						updateDimensions={updateDimensions}
						isInFullScreen={isInFullScreen}
						containerRef={frame}
						selectedVideoMode={selectedVideoMode}
					/>
				);
			}

			default:
				return null;
		}
	};

	useEffect(() => {
		const calculateDimensions = () => {
			if (calculateAspectDimensions()) {
				setInitDimensionsDetected(true);
			} else {
				// Dimensions could not be determined yet, we try again after 200 ms.
				setTimeout(calculateDimensions, 200);
			}
		};

		calculateDimensions();
	}, []);

	useEffect(() => {
		calculateAspectDimensions(isInFullScreen);
	}, [size, isInFullScreen]);

	const calculateAspectDimensions = (isVideoInFullScreen = false) => {
		// full-screen aspect ratio
		if (isVideoInFullScreen) {
			const container = bccContainerRef.current;
			if (!container?.clientWidth || !container.clientHeight || !frame.current) {
				return false;
			}

			// Measure the actual fullscreen player instead of the window. During the
			// fullscreen transition those dimensions can differ by a pixel, which can
			// leave the accelerated video and UI overlays on mismatched compositor layers.
			const { width, height } = fitAspectInsideContainer(container, 16, 9);
			frame.current.style.width = `${width}px`;
			frame.current.style.height = `${height}px`;

			setResizeTo("fullscreen");
		} else {
			if (frameStartHeight.current) {
				frame.current.style.height = `${frameStartHeight.current}px`;
			} else {
				frameStartHeight.current = frame.current?.clientHeight;
			}
			const container = bccContainerRef.current;
			if (container?.clientWidth) {
				if (inDock && dockContainerDims.width !== "" && dockContainerDims.height !== "") {
					frame.current.style.width = dockContainerDims.width;
					frame.current.style.height = dockContainerDims.height;
				} else {
					const { width, height } = fitAspectInsideContainer(container, 16, 9);
					frame.current.style.width = `${width}px`;
					frame.current.style.height = `${height}px`;
				}
				const frameHeight = frame.current.style.height.replace(/px$/, "");
				if (inDock && Number(frameHeight) < 220) {
					setDockContainerDims({ width: frame.current.style.width, height: frame.current.style.height });
				}
				setResizeTo("dock");
			} else {
				return false;
			}
		}

		// Set height and width of nested video tag with class 'video-element' to match frame
		// const videoEl = frame.current?.querySelector(".video-element");
		// if (videoEl) {
		// 	videoEl.style.width = frame.current.style.width;
		// 	videoEl.style.height = frame.current.style.height;
		// }

		forceUpdate(prevCount => prevCount + 1);
		return true;
	};

	const getSelectedVideoMode = videoMode => {
		const nextMode = videoMode === "Live" ? "Live" : "recorded";
		if (selectedVideoMode.toLowerCase() !== nextMode.toLowerCase()) {
			logVideoPlayerAction("video-player-mode-changed", {
				fromMode: selectedVideoMode.toLowerCase(),
				toMode: nextMode.toLowerCase()
			});
		}
		if (videoMode === "Live") {
			setSelectedClip(null);
			setSelectedVideoMode("Live");
			return;
		}

		setSelectedVideoMode("recorded");
	};

	const drawAspectRatio = () => {
		const contents = docked ? (
			<Typography style={{ position: "absolute" }} variant="subtitle1">
				{docked ? (
					<Translate value="global.CBComponents.video.videoPlayer.camInDock" />
				) : (
					<Translate value="global.CBComponents.video.videoPlayer.camInModal" />
				)}
			</Typography>
		) : (
			getPlayer()
		);

		return (
			<div
				className="bccVideoPlayerContainer"
				style={{ height: "100%", width: "100%", zIndex: dockData.isOpen ? 500 : 1000 }}
				ref={bccContainerRef}
			>
				{!preload && (
					<VideoTopBar
						camera={camera}
						namePrefix={namePrefix}
						dockedItemId={dockedItemId}
						isDockedVideoTour={isDockedVideoTour}
						tourMode={tourMode}
						tourPaused={tourPaused}
						readOnly={readOnly}
						getSelectedVideoMode={getSelectedVideoMode}
						isInFullScreen={isInFullScreen}
						expanded={expanded}
						inDock={inDock}
						isInVideoWall={isInVideoWall}
						removeFromWall={removeFromWall}
						toggleFullScreenCameraDialog={toggleFullScreenCameraDialog}
						currentVideoMode={selectedVideoMode}
						clusterProps={clusterProps}
						isOnAlertProfile={isOnAlertProfile}
						hideRemoveFromWall={hideRemoveFromWall}
						setTourPaused={setTourPaused}
						videoPlayerHovered={videoPlayerHovered}
						instanceId={instanceId}
						disableVideoModeToggle={disableVideoModeToggle}
						onBookmarkSelect={handleBookmarkSelect}
						onClipSelect={handleClipSelect}
						dialogContainerRef={dialogContainerRef} data-testid="orion-components-cbcomponents-new-video-player-live-video-player-video-top-bar"
					/>
				)}
				<div
					style={{
						width: "100%",
						height: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center"
					}}
					className="center bccVideoContent"
				>
					<div ref={frame} style={styles.frame}>
						<div className="bcc-maintain-height-aspect-ratio-box">
							{initDimensionsDetected &&
							hasFeature("control") &&
							hasFeature("video-integrated-control") &&
							!preload &&
							ptzLockEnabled ? (
								<VideoIntegratedControls
									camera={camera}
									instanceId={instanceId}
									customStyles={inDock ? { top: 0, left: 0 } : {}}
								/>
							) : (
								<></>
							)}
							{initDimensionsDetected && <div className="bcc-aspect-ratio-box-inside">{contents}</div>}
						</div>
					</div>
				</div>
				{!preload && !tourMode && initDimensionsDetected && (
					<VideoBottomBar
						selectedVideoMode={selectedVideoMode}
						camera={camera}
						contextId={entityId}
						playbackStreamName={playbackStreamName}
						dialogContainerRef={dialogContainerRef}
						isOnAlertProfile={isOnAlertProfile}
						instanceId={instanceId}
						inDock={inDock}
						alertDateTime={alertDateTime}
						hasControlFeature={hasFeature("control")}
						defaultToRecorded={defaultToRecorded}
						playbackStartOffset={playbackStartOffset}
						initialRecordedDuration={initialRecordedDuration}
						initialRecordedTimeUnit={initialRecordedTimeUnit}
						registerRecordedNavigator={registerRecordedNavigator}
						selectedClip={selectedClip}
						onRecordedTimeframeChange={handleRecordedTimeframeChange} data-testid="orion-components-cbcomponents-new-video-player-live-video-player-video-bottom-bar"
					/>
				)}
			</div>
		);
	};

	return <>{drawAspectRatio()}</>;
};

LiveVideoPlayer.propTypes = propTypes;
LiveVideoPlayer.defaultProps = defaultProps;

export default LiveVideoPlayer;
